import React, {
    Fragment,
    useMemo,
    useState
} from "react";

import axios from "axios";
import swal from "sweetalert";


/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
    "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";


/* =========================================================
   STRONG PASSWORD RULES
========================================================= */

const getPasswordRules = (password = "") => ({
    minLength: password.length >= 8,
    maxLength: password.length <= 15,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password)
});

const isStrongPassword = (password = "") => {
    const rules = getPasswordRules(password);

    return (
        password.length > 0 &&
        rules.minLength &&
        rules.maxLength &&
        rules.lowercase &&
        rules.uppercase &&
        rules.number &&
        rules.special
    );
};

const getPasswordStrength = (password = "") => {
    if (!password) {
        return {
            score: 0,
            label: "",
            className: ""
        };
    }

    const rules = getPasswordRules(password);
    let score = 0;

    if (rules.minLength && rules.maxLength) score++;
    if (rules.lowercase && rules.uppercase) score++;
    if (rules.number && rules.special) score++;

    if (score <= 1) {
        return { score: 1, label: "Weak", className: "weak" };
    }

    if (score === 2) {
        return { score: 2, label: "Good", className: "good" };
    }

    return { score: 3, label: "Strong", className: "strong" };
};


/* =========================================================
   COMPONENT
========================================================= */

const Checkout = () => {


    /* =====================================================
       FORM STATE
    ===================================================== */

    const [depositInput, setDepositInput] =
        useState({

            current_password: "",

            new_password: "",

            new_confirm_password: "",

            error_list: {}
        });


    /* =====================================================
       LOADING
    ===================================================== */

    const [loading, setLoading] =
        useState(false);


    /* =====================================================
       SHOW / HIDE PASSWORD
    ===================================================== */

    const [showPassword, setShowPassword] =
        useState({

            current: false,

            new: false,

            confirm: false

        });


    /* =====================================================
       INPUT
    ===================================================== */

    const handleInput = (e) => {

        const {
            name,
            value
        } = e.target;


        setDepositInput(
            (previous) => ({

                ...previous,

                [name]: value,

                error_list: {

                    ...previous.error_list,

                    [name]: ""

                }

            })
        );
    };


    /* =====================================================
       TOGGLE PASSWORD
    ===================================================== */

    const togglePassword =
        (field) => {

            setShowPassword(
                (previous) => ({

                    ...previous,

                    [field]:
                        !previous[field]

                })
            );
        };


    /* =====================================================
       PASSWORD STRENGTH
    ===================================================== */

    const passwordStrength =
        useMemo(
            () =>
                getPasswordStrength(
                    depositInput.new_password
                ),
            [
                depositInput.new_password
            ]
        );


    /* =====================================================
       STRONG PASSWORD RULE STATUS
    ===================================================== */

    const passwordRules =
        useMemo(
            () =>
                getPasswordRules(
                    depositInput.new_password
                ),
            [depositInput.new_password]
        );


    const strongPasswordReady =
        useMemo(
            () =>
                isStrongPassword(
                    depositInput.new_password
                ),
            [depositInput.new_password]
        );


    /* =====================================================
       PASSWORD MATCH
    ===================================================== */

    const passwordMatch =
        useMemo(() => {

            if (
                !depositInput.new_password ||
                !depositInput.new_confirm_password
            ) {

                return null;
            }


            return (
                depositInput.new_password ===
                depositInput.new_confirm_password
            );

        }, [

            depositInput.new_password,

            depositInput.new_confirm_password

        ]);


    /* =====================================================
       SUBMIT
    ===================================================== */

    const depositSubmit =
        async (e) => {

            e.preventDefault();


            if (loading) {
                return;
            }


            /* =============================================
               AUTH TOKEN
            ============================================= */

            const auth_token =
                localStorage.getItem(
                    "auth_token"
                );


            if (!auth_token) {

                swal(
                    "Warning",
                    "Please login again.",
                    "warning"
                );

                return;
            }


            /* =============================================
               FRONTEND BASIC VALIDATION
            ============================================= */

            const errors = {};


            if (
                !depositInput.current_password
            ) {

                errors.current_password =
                    "Current password is required.";
            }


            if (
                !depositInput.new_password
            ) {

                errors.new_password =
                    "New password is required.";
            }


            if (
                depositInput.new_password &&
                !isStrongPassword(
                    depositInput.new_password
                )
            ) {

                errors.new_password =
                    "Password must be 8-15 characters and include uppercase, lowercase, number and special character.";
            }


            if (
                !depositInput.new_confirm_password
            ) {

                errors.new_confirm_password =
                    "Please confirm your new password.";
            }


            if (
                depositInput.new_password &&
                depositInput.new_confirm_password &&
                depositInput.new_password !==
                    depositInput.new_confirm_password
            ) {

                errors.new_confirm_password =
                    "New passwords do not match.";
            }


            if (
                Object.keys(errors).length > 0
            ) {

                setDepositInput(
                    (previous) => ({

                        ...previous,

                        error_list: errors

                    })
                );

                return;
            }


            /* =============================================
               REQUEST DATA
            ============================================= */

            const data = {

                current_password:
                    depositInput.current_password,

                new_password:
                    depositInput.new_password,

                new_confirm_password:
                    depositInput.new_confirm_password,

                key_s:
                    API_KEY

            };


            /* =============================================
               API REQUEST
            ============================================= */

            try {

                setLoading(true);


                const res =
                    await axios.post(
                        "/apps-password-change",
                        data
                    );


                /* =========================================
                   SUCCESS
                ========================================= */

                if (
                    res?.data?.status ===
                    "success"
                ) {

                    swal(
                        "Success",
                        res.data.message ||
                            "Password changed successfully.",
                        "success"
                    );


                    setDepositInput({

                        current_password: "",

                        new_password: "",

                        new_confirm_password: "",

                        error_list: {}

                    });


                    setShowPassword({

                        current: false,

                        new: false,

                        confirm: false

                    });

                }


                /* =========================================
                   FAIL
                ========================================= */

                else if (
                    res?.data?.status ===
                    "fail"
                ) {

                    swal(
                        "Warning",
                        res.data.message ||
                            "Unable to change password.",
                        "warning"
                    );

                }


                /* =========================================
                   VALIDATION ERROR
                ========================================= */

                else {

                    const validationErrors =
                        res?.data
                            ?.validation_error ||
                        {};


                    setDepositInput(
                        (previous) => ({

                            ...previous,

                            error_list:
                                validationErrors

                        })
                    );
                }


            } catch (error) {

                console.error(
                    "Password Change Error:",
                    error
                );


                /* =========================================
                   LARAVEL 422 VALIDATION
                ========================================= */

                if (
                    error?.response?.status ===
                    422
                ) {

                    const validationErrors =
                        error?.response?.data
                            ?.errors ||
                        error?.response?.data
                            ?.validation_error ||
                        {};


                    setDepositInput(
                        (previous) => ({

                            ...previous,

                            error_list:
                                validationErrors

                        })
                    );


                    return;
                }


                /* =========================================
                   GENERAL ERROR
                ========================================= */

                swal(
                    "Error",
                    error?.response?.data
                        ?.message ||
                        "Something went wrong. Please try again.",
                    "error"
                );


            } finally {

                setLoading(false);
            }
        };


    /* =====================================================
       RENDER
    ===================================================== */

    return (

        <Fragment>


            {/* =================================================
                CSS
            ================================================= */}

            <style>{`

                * {
                    box-sizing: border-box;
                }


                .password-change-page {

                    --primary: #5d50f5;
                    --primary-dark: #493be7;
                    --primary-soft: #f2f0ff;

                    --green: #12b76a;
                    --green-dark: #067647;
                    --green-soft: #ecfdf3;

                    --orange: #f79009;
                    --orange-dark: #b54708;
                    --orange-soft: #fff7ed;

                    --red: #f04438;
                    --red-dark: #b42318;
                    --red-soft: #fef3f2;

                    --text: #101828;
                    --text2: #344054;
                    --muted: #667085;

                    --border: #e4e7ec;

                    width: 100%;

                    padding: 20px;

                    border-radius: 20px;

                    background: #ffffff;

                    color: var(--text);

                    font-family:
                        Inter,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        Arial,
                        sans-serif;
                }


                .password-change-container {

                    width: 100%;

                    max-width: 760px;

                    margin: 0 auto;
                }


                /* =============================================
                   HEADER
                ============================================= */

                .password-page-header {

                    margin-bottom: 22px;
                }


                .password-page-title {

                    display: flex;

                    align-items: center;

                    gap: 14px;
                }


                .password-header-icon {

                    width: 50px;
                    height: 50px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 14px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 21px;
                }


                .password-page-title h2 {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 26px;

                    line-height: 1.25;

                    font-weight: 800;
                }


                .password-page-title p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 13px;

                    line-height: 1.5;
                }


                /* =============================================
                   CARD
                ============================================= */

                .password-card {

                    overflow: hidden;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 18px;

                    background: #ffffff;

                    box-shadow:
                        0 8px 30px
                        rgba(
                            16,
                            24,
                            40,
                            .06
                        );
                }


                .password-card-header {

                    padding: 20px 22px;

                    border-bottom:
                        1px solid
                        #eef0f4;
                }


                .password-card-header h3 {

                    margin:
                        0 0 5px;

                    color:
                        var(--text);

                    font-size: 17px;

                    font-weight: 800;
                }


                .password-card-header p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 12px;

                    line-height: 1.5;
                }


                .password-card-body {

                    padding: 24px;
                }


                /* =============================================
                   SECURITY NOTICE
                ============================================= */

                .password-security-note {

                    display: flex;

                    align-items:
                        flex-start;

                    gap: 12px;

                    padding: 14px;

                    margin-bottom: 24px;

                    border:
                        1px solid
                        #d9d6fe;

                    border-radius: 12px;

                    background:
                        #f9f8ff;
                }


                .password-security-note-icon {

                    width: 34px;
                    height: 34px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 9px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);
                }


                .password-security-note strong {

                    display: block;

                    margin-bottom: 3px;

                    color:
                        var(--text2);

                    font-size: 13px;

                    font-weight: 800;
                }


                .password-security-note p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 11px;

                    line-height: 1.5;
                }


                /* =============================================
                   FORM
                ============================================= */

                .password-form-group {

                    margin-bottom: 20px;
                }


                .password-form-label {

                    display: block;

                    margin-bottom: 8px;

                    color:
                        var(--text2);

                    font-size: 13px;

                    font-weight: 700;
                }


                .password-required {

                    color:
                        var(--red);
                }


                .password-input-wrapper {

                    position: relative;

                    width: 100%;
                }


                .password-input-icon {

                    position: absolute;

                    top: 50%;
                    left: 15px;

                    transform:
                        translateY(-50%);

                    color:
                        #98a2b3;

                    font-size: 15px;

                    pointer-events: none;
                }


                .password-form-input {

                    width: 100%;

                    height: 50px;

                    padding:
                        0 50px
                        0 44px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 11px;

                    outline: none;

                    background:
                        #ffffff;

                    color:
                        var(--text);

                    font-size: 14px;

                    transition:
                        border-color .2s ease,
                        box-shadow .2s ease,
                        background .2s ease;
                }


                .password-form-input::placeholder {

                    color:
                        #98a2b3;
                }


                .password-form-input:hover {

                    border-color:
                        #cbd0d9;
                }


                .password-form-input:focus {

                    border-color:
                        var(--primary);

                    box-shadow:
                        0 0 0 4px
                        rgba(
                            93,
                            80,
                            245,
                            .08
                        );
                }


                .password-form-input.has-error {

                    border-color:
                        var(--red);

                    background:
                        #fffafa;
                }


                .password-toggle {

                    position: absolute;

                    top: 50%;
                    right: 8px;

                    width: 38px;
                    height: 38px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    transform:
                        translateY(-50%);

                    border: 0;

                    border-radius: 8px;

                    outline: none;

                    background:
                        transparent;

                    color:
                        #667085;

                    cursor: pointer;

                    font-size: 15px;

                    transition:
                        .15s ease;
                }


                .password-toggle:hover {

                    background:
                        #f2f4f7;

                    color:
                        var(--primary);
                }


                /* =============================================
                   ERRORS
                ============================================= */

                .password-error {

                    display: flex;

                    align-items: center;

                    gap: 5px;

                    margin-top: 6px;

                    color:
                        var(--red-dark);

                    font-size: 11px;

                    font-weight: 600;
                }


                /* =============================================
                   PASSWORD STRENGTH
                ============================================= */

                .password-strength {

                    margin-top: 10px;
                }


                .password-strength-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 10px;

                    margin-bottom: 6px;
                }


                .password-strength-header span {

                    color:
                        var(--muted);

                    font-size: 11px;
                }


                .password-strength-label {

                    font-weight: 800;
                }


                .password-strength-label.weak {

                    color:
                        var(--red);
                }


                .password-strength-label.good {

                    color:
                        var(--orange-dark);
                }


                .password-strength-label.strong {

                    color:
                        var(--green-dark);
                }


                .password-strength-bars {

                    display: grid;

                    grid-template-columns:
                        repeat(3, 1fr);

                    gap: 5px;
                }


                .password-strength-bar {

                    height: 4px;

                    border-radius: 50px;

                    background:
                        #eaecf0;
                }


                .password-strength-bar.weak {

                    background:
                        var(--red);
                }


                .password-strength-bar.good {

                    background:
                        var(--orange);
                }


                .password-strength-bar.strong {

                    background:
                        var(--green);
                }


                /* =============================================
                   MATCH MESSAGE
                ============================================= */

                .password-match-message {

                    display: flex;

                    align-items: center;

                    gap: 5px;

                    margin-top: 7px;

                    font-size: 11px;

                    font-weight: 700;
                }


                .password-match-message.success {

                    color:
                        var(--green-dark);
                }


                .password-match-message.error {

                    color:
                        var(--red-dark);
                }


                /* =============================================
                   PASSWORD TIPS
                ============================================= */

                .password-tips {

                    padding: 14px 16px;

                    margin:
                        5px 0 22px;

                    border-radius: 12px;

                    background:
                        #f9fafb;

                    border:
                        1px solid
                        #eef0f4;
                }


                .password-tips-title {

                    display: block;

                    margin-bottom: 8px;

                    color:
                        var(--text2);

                    font-size: 12px;

                    font-weight: 800;
                }


                .password-tip {

                    position: relative;

                    display: block;

                    padding-left: 17px;

                    margin-bottom: 5px;

                    color:
                        var(--muted);

                    font-size: 11px;

                    line-height: 1.45;
                }


                .password-tip:last-child {

                    margin-bottom: 0;
                }


                .password-tip::before {

                    content: "•";

                    position: absolute;

                    left: 3px;

                    color:
                        var(--primary);

                    font-weight: 800;
                }


                .password-tip.rule {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding-left: 0;
                }

                .password-tip.rule::before {
                    display: none;
                }

                .password-tip.rule i {
                    width: 15px;
                    flex-shrink: 0;
                    font-size: 12px;
                }

                .password-tip.rule.valid {
                    color: var(--green-dark);
                    font-weight: 700;
                }

                .password-tip.rule.invalid {
                    color: var(--muted);
                }

                /* =============================================
                   SUBMIT
                ============================================= */

                .password-submit-wrapper {

                    padding-top: 4px;
                }


                .password-submit-button {

                    width: 100%;
                    height: 50px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    gap: 9px;

                    border: 0;

                    border-radius: 11px;

                    outline: none;

                    background:
                        var(--primary);

                    color: #ffffff;

                    font-size: 14px;

                    font-weight: 800;

                    cursor: pointer;

                    transition:
                        .2s ease;

                    box-shadow:
                        0 7px 18px
                        rgba(
                            93,
                            80,
                            245,
                            .18
                        );
                }


                .password-submit-button:hover:not(:disabled) {

                    background:
                        var(--primary-dark);

                    transform:
                        translateY(-1px);
                }


                .password-submit-button:disabled {

                    opacity: .65;

                    cursor: not-allowed;

                    transform: none;
                }


                .password-loading-spinner {

                    width: 17px;
                    height: 17px;

                    border:
                        2px solid
                        rgba(
                            255,
                            255,
                            255,
                            .4
                        );

                    border-top-color:
                        #ffffff;

                    border-radius:
                        50%;

                    animation:
                        passwordSpin
                        .7s linear
                        infinite;
                }


                @keyframes passwordSpin {

                    to {

                        transform:
                            rotate(360deg);
                    }
                }


                /* =============================================
                   FOOTER
                ============================================= */

                .password-footer-note {

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    gap: 6px;

                    margin-top: 15px;

                    color:
                        var(--muted);

                    font-size: 11px;

                    text-align: center;
                }


                /* =============================================
                   RESPONSIVE
                ============================================= */

                @media (
                    max-width: 767px
                ) {

                    .password-change-page {

                        padding: 14px;

                        border-radius: 14px;
                    }


                    .password-page-title {

                        align-items:
                            flex-start;
                    }


                    .password-header-icon {

                        width: 44px;
                        height: 44px;

                        border-radius: 12px;

                        font-size: 18px;
                    }


                    .password-page-title h2 {

                        font-size: 22px;
                    }


                    .password-card-body {

                        padding: 18px;
                    }


                    .password-card-header {

                        padding: 17px 18px;
                    }

                }


                @media (
                    max-width: 420px
                ) {

                    .password-change-page {

                        padding: 10px;
                    }


                    .password-page-title h2 {

                        font-size: 20px;
                    }


                    .password-card-body {

                        padding: 15px;
                    }


                    .password-security-note {

                        padding: 12px;
                    }

                }

            `}</style>


            {/* =================================================
                PAGE
            ================================================= */}

            <main
                className="password-change-page"
            >

                <div
                    className="password-change-container"
                >


                    {/* =========================================
                        PAGE HEADER
                    ========================================= */}

                    <div
                        className="password-page-header"
                    >

                        <div
                            className="password-page-title"
                        >

                            <div
                                className="password-header-icon"
                            >
                                <i
                                    className="fas fa-lock"
                                    aria-hidden="true"
                                ></i>
                            </div>


                            <div>

                                <h2>
                                    Change Password
                                </h2>

                                <p>
                                    Update your account password
                                    and keep your account secure.
                                </p>

                            </div>

                        </div>

                    </div>


                    {/* =========================================
                        CARD
                    ========================================= */}

                    <div
                        className="password-card"
                    >


                        {/* CARD HEADER */}

                        <div
                            className="password-card-header"
                        >

                            <h3>
                                Password Settings
                            </h3>

                            <p>
                                Enter your current password and
                                choose a new secure password.
                            </p>

                        </div>


                        {/* CARD BODY */}

                        <div
                            className="password-card-body"
                        >


                            {/* =================================
                                SECURITY NOTE
                            ================================= */}

                            <div
                                className="password-security-note"
                            >

                                <div
                                    className="password-security-note-icon"
                                >
                                    <i
                                        className="fas fa-shield"
                                        aria-hidden="true"
                                    ></i>
                                </div>


                                <div>

                                    <strong>
                                        Secure your account
                                    </strong>

                                    <p>
                                        Use a strong password that
                                        you do not use for other
                                        accounts.
                                    </p>

                                </div>

                            </div>


                            {/* =================================
                                FORM
                            ================================= */}

                            <form
                                onSubmit={
                                    depositSubmit
                                }
                                noValidate
                            >


                                {/* =============================
                                    CURRENT PASSWORD
                                ============================= */}

                                <div
                                    className="password-form-group"
                                >

                                    <label
                                        className="password-form-label"
                                        htmlFor="current_password"
                                    >
                                        Current Password{" "}

                                        <span
                                            className="password-required"
                                        >
                                            *
                                        </span>
                                    </label>


                                    <div
                                        className="password-input-wrapper"
                                    >

                                        <span
                                            className="password-input-icon"
                                        >
                                            <i
                                                className="fas fa-lock"
                                                aria-hidden="true"
                                            ></i>
                                        </span>


                                        <input
                                            type={
                                                showPassword.current
                                                    ? "text"
                                                    : "password"
                                            }
                                            id="current_password"
                                            name="current_password"
                                            className={
                                                `password-form-input ${
                                                    depositInput
                                                        .error_list
                                                        ?.current_password
                                                        ? "has-error"
                                                        : ""
                                                }`
                                            }
                                            placeholder="Enter current password"
                                            value={
                                                depositInput
                                                    .current_password
                                            }
                                            onChange={
                                                handleInput
                                            }
                                            autoComplete="current-password"
                                        />


                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() =>
                                                togglePassword(
                                                    "current"
                                                )
                                            }
                                            aria-label="Show or hide current password"
                                        >

                                            <i
                                                className={
                                                    showPassword.current
                                                        ? "fas fa-eye-slash"
                                                        : "fas fa-eye"
                                                }
                                                aria-hidden="true"
                                            ></i>

                                        </button>

                                    </div>


                                    {depositInput
                                        .error_list
                                        ?.current_password && (

                                        <div
                                            className="password-error"
                                        >

                                            <i
                                                className="fas fa-exclamation-circle"
                                                aria-hidden="true"
                                            ></i>

                                            <span>
                                                {
                                                    depositInput
                                                        .error_list
                                                        .current_password
                                                }
                                            </span>

                                        </div>
                                    )}

                                </div>


                                {/* =============================
                                    NEW PASSWORD
                                ============================= */}

                                <div
                                    className="password-form-group"
                                >

                                    <label
                                        className="password-form-label"
                                        htmlFor="new_password"
                                    >
                                        New Password{" "}

                                        <span
                                            className="password-required"
                                        >
                                            *
                                        </span>
                                    </label>


                                    <div
                                        className="password-input-wrapper"
                                    >

                                        <span
                                            className="password-input-icon"
                                        >
                                            <i
                                                className="fas fa-key"
                                                aria-hidden="true"
                                            ></i>
                                        </span>


                                        <input
                                            type={
                                                showPassword.new
                                                    ? "text"
                                                    : "password"
                                            }
                                            id="new_password"
                                            name="new_password"
                                            className={
                                                `password-form-input ${
                                                    depositInput
                                                        .error_list
                                                        ?.new_password
                                                        ? "has-error"
                                                        : ""
                                                }`
                                            }
                                            placeholder="Enter new password"
                                            value={
                                                depositInput
                                                    .new_password
                                            }
                                            onChange={
                                                handleInput
                                            }
                                            autoComplete="new-password"
                                        />


                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() =>
                                                togglePassword(
                                                    "new"
                                                )
                                            }
                                            aria-label="Show or hide new password"
                                        >

                                            <i
                                                className={
                                                    showPassword.new
                                                        ? "fas fa-eye-slash"
                                                        : "fas fa-eye"
                                                }
                                                aria-hidden="true"
                                            ></i>

                                        </button>

                                    </div>


                                    {depositInput
                                        .error_list
                                        ?.new_password && (

                                        <div
                                            className="password-error"
                                        >

                                            <i
                                                className="fas fa-exclamation-circle"
                                                aria-hidden="true"
                                            ></i>

                                            <span>
                                                {
                                                    depositInput
                                                        .error_list
                                                        .new_password
                                                }
                                            </span>

                                        </div>
                                    )}


                                    {/* PASSWORD STRENGTH */}

                                    {depositInput
                                        .new_password && (

                                        <div
                                            className="password-strength"
                                        >

                                            <div
                                                className="password-strength-header"
                                            >

                                                <span>
                                                    Password strength
                                                </span>


                                                <span
                                                    className={
                                                        `password-strength-label ${passwordStrength.className}`
                                                    }
                                                >
                                                    {
                                                        passwordStrength.label
                                                    }
                                                </span>

                                            </div>


                                            <div
                                                className="password-strength-bars"
                                            >

                                                {[1, 2, 3].map(
                                                    (bar) => (

                                                        <span
                                                            key={
                                                                bar
                                                            }
                                                            className={
                                                                `password-strength-bar ${
                                                                    bar <=
                                                                    passwordStrength.score
                                                                        ? passwordStrength.className
                                                                        : ""
                                                                }`
                                                            }
                                                        ></span>

                                                    )
                                                )}

                                            </div>

                                        </div>
                                    )}

                                </div>


                                {/* =============================
                                    CONFIRM PASSWORD
                                ============================= */}

                                <div
                                    className="password-form-group"
                                >

                                    <label
                                        className="password-form-label"
                                        htmlFor="new_confirm_password"
                                    >
                                        Confirm New Password{" "}

                                        <span
                                            className="password-required"
                                        >
                                            *
                                        </span>
                                    </label>


                                    <div
                                        className="password-input-wrapper"
                                    >

                                        <span
                                            className="password-input-icon"
                                        >
                                            <i
                                                className="fas fa-check-circle"
                                                aria-hidden="true"
                                            ></i>
                                        </span>


                                        <input
                                            type={
                                                showPassword.confirm
                                                    ? "text"
                                                    : "password"
                                            }
                                            id="new_confirm_password"
                                            name="new_confirm_password"
                                            className={
                                                `password-form-input ${
                                                    depositInput
                                                        .error_list
                                                        ?.new_confirm_password
                                                        ? "has-error"
                                                        : ""
                                                }`
                                            }
                                            placeholder="Confirm new password"
                                            value={
                                                depositInput
                                                    .new_confirm_password
                                            }
                                            onChange={
                                                handleInput
                                            }
                                            autoComplete="new-password"
                                        />


                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() =>
                                                togglePassword(
                                                    "confirm"
                                                )
                                            }
                                            aria-label="Show or hide confirm password"
                                        >

                                            <i
                                                className={
                                                    showPassword.confirm
                                                        ? "fas fa-eye-slash"
                                                        : "fas fa-eye"
                                                }
                                                aria-hidden="true"
                                            ></i>

                                        </button>

                                    </div>


                                    {depositInput
                                        .error_list
                                        ?.new_confirm_password ? (

                                        <div
                                            className="password-error"
                                        >

                                            <i
                                                className="fas fa-exclamation-circle"
                                                aria-hidden="true"
                                            ></i>

                                            <span>
                                                {
                                                    depositInput
                                                        .error_list
                                                        .new_confirm_password
                                                }
                                            </span>

                                        </div>

                                    ) : passwordMatch === true ? (

                                        <div
                                            className="password-match-message success"
                                        >

                                            <i
                                                className="fas fa-check-circle"
                                                aria-hidden="true"
                                            ></i>

                                            Passwords match

                                        </div>

                                    ) : passwordMatch === false ? (

                                        <div
                                            className="password-match-message error"
                                        >

                                            <i
                                                className="fas fa-times-circle"
                                                aria-hidden="true"
                                            ></i>

                                            Passwords do not match

                                        </div>

                                    ) : null}

                                </div>


                                {/* =============================
                                    PASSWORD TIPS
                                ============================= */}

                                <div
                                    className="password-tips"
                                >

                                    <span
                                        className="password-tips-title"
                                    >
                                        Strong password requirements
                                    </span>

                                    <span className={`password-tip rule ${passwordRules.minLength && passwordRules.maxLength ? "valid" : "invalid"}`}>
                                        <i className={passwordRules.minLength && passwordRules.maxLength ? "fas fa-check-circle" : "fas fa-circle"}></i>
                                        8-15 characters
                                    </span>

                                    <span className={`password-tip rule ${passwordRules.uppercase ? "valid" : "invalid"}`}>
                                        <i className={passwordRules.uppercase ? "fas fa-check-circle" : "fas fa-circle"}></i>
                                        At least one uppercase letter (A-Z)
                                    </span>

                                    <span className={`password-tip rule ${passwordRules.lowercase ? "valid" : "invalid"}`}>
                                        <i className={passwordRules.lowercase ? "fas fa-check-circle" : "fas fa-circle"}></i>
                                        At least one lowercase letter (a-z)
                                    </span>

                                    <span className={`password-tip rule ${passwordRules.number ? "valid" : "invalid"}`}>
                                        <i className={passwordRules.number ? "fas fa-check-circle" : "fas fa-circle"}></i>
                                        At least one number (0-9)
                                    </span>

                                    <span className={`password-tip rule ${passwordRules.special ? "valid" : "invalid"}`}>
                                        <i className={passwordRules.special ? "fas fa-check-circle" : "fas fa-circle"}></i>
                                        At least one special character (!@#$% etc.)
                                    </span>

                                </div>


                                {/* =============================
                                    SUBMIT
                                ============================= */}

                                <div
                                    className="password-submit-wrapper"
                                >

                                    <button
                                        type="submit"
                                        className="password-submit-button"
                                        disabled={
                                            loading ||
                                            !depositInput.current_password ||
                                            !strongPasswordReady ||
                                            passwordMatch !== true
                                        }
                                    >

                                        {loading ? (

                                            <Fragment>

                                                <span
                                                    className="password-loading-spinner"
                                                ></span>

                                                Updating Password...

                                            </Fragment>

                                        ) : (

                                            <Fragment>

                                                <i
                                                    className="fas fa-shield"
                                                    aria-hidden="true"
                                                ></i>

                                                Change Password

                                            </Fragment>
                                        )}

                                    </button>

                                </div>

                            </form>


                            {/* =================================
                                FOOTER
                            ================================= */}

                            <div
                                className="password-footer-note"
                            >

                                <i
                                    className="fas fa-lock"
                                    aria-hidden="true"
                                ></i>

                                Your password is securely processed.

                            </div>

                        </div>

                    </div>

                </div>

            </main>

        </Fragment>
    );
};


export default Checkout;